import React, { useEffect, useState } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";

import { parse } from "query-string";

import I18n from "../../I18n";
import theme from "../../style/_theme.json";
import proxy from "../../routes/proxy";

import { mapProperties, stringifyFilter } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
} from "../../store/actions";

import { ImageRender } from "colbi_web_ui/lib/components";
import AsideMenu from "colbi_web_ui/lib/components/layout/AsideMenu/AsideMenu";
import Header from "colbi_web_ui/lib/components/layout/Header/Header";

import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";
import { GlobalProvider } from "../../state/globalProvider";
import ModalController from "../App/ModalController/ModalController";

import { manageNavigation } from "./navigation";

import styles from "./OverviewDashboard.module.sass";

import { UserRoles } from "../../enums/userRoles";
import DialogController from "../App/DialogController/DialogController";
import { checkRolesByOrganization } from "colbi_web_ui/lib/utils/roleValidator";
import configsByTheme from "../../enums/configsByTheme";
import { getOrganizationName } from "../../utils/camelize";
import { submitTicketFormData } from "../../store/actions/modalActions";
import { changeDeepObject } from "../../utils";
import { REACT_APP_COLBI_THEME } from "../../environmentVariables";
import { decodeUserSession } from "../../utils/utils";

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const appTheme = REACT_APP_COLBI_THEME ? REACT_APP_COLBI_THEME.toLocaleLowerCase() : "";
const asideMenuLogo = configsByTheme(appTheme)["icon"];
const localesByAppTheme = configsByTheme(appTheme)["locales"];
const logoHorizontalDisplay = configsByTheme(appTheme)["logoHorizontalDisplay"] || false;
const freshDeskFormTitleByAppTheme = configsByTheme(appTheme)["freshDeskFormTitle"];

const logo = () => {
    return (
        appTheme && <ImageRender icon={`${appTheme && appTheme.length ? asideMenuLogo : "logo"}`} />
    );
};

const OverviewDashboard = (props) => {
    const {
        user,
        fetch,
        match,
        setLocale,
        location,
        toggleMenu,
        logout,
        history,
        pushModal,
        popModal,
        menu,
        reload,
        pushDialog,
        uploads,
        uploadFiles,
        submitAuditNote,
        submitTicket,
    } = props;

    const { organizationId, fiscalYear, period } = match.params;
    const userInformation = decodeUserSession(user);
    const validUser = checkRolesByOrganization(userInformation, organizationId, [
        UserRoles.WRITER,
        UserRoles.ADMIN,
    ]);
    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const [hover, setHover] = useState(false);

    if (!userInformation) {
        return <Redirect to={"/login"} push />;
    }

    if (userInformation && userInformation.expired) {
        return <Redirect to={`/${locale}/reset_password`} />;
    }

    if (userInformation && !fiscalYear) {
        return <Redirect to={`/${locale}/not_found`} />;
    }

    if (userInformation && !validUser) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    let navigation = manageNavigation(i18n);

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const version = (fetch.results[`${locale}_application_version`] || "").version;

    const ticketSubjectEnvironment = fetch.results[`${locale}_get_environment_to_subject`] || "";

    const isToDisplaySupportIcon =
        (fetch.results[`${locale}_is_to_display_support_icon`] || "false").toLocaleLowerCase() ===
        "true";

    const genericOverview =
        fetch.results[
            `${locale}_generic_organization_dashboard_fiscalYear_${fiscalYear}_organizationId_${organizationId}${
                period !== "full" ? `_period_${period}` : ""
            }`
        ];

    const getLayout = JSON.parse(genericOverview || "[]");

    changeDeepObject(getLayout, ["component", "cards"]);

    let displayComponents = [];

    if (getLayout.length > 0) {
        displayComponents.push(...getLayout);
    }

    const children = displayComponents.map((c, index) => {
        const Component = c.component;
        const componentProps = mapProperties(
            c.props,
            (fetch || {}).results,
            (fetch || {}).status,
            [],
            props,
            params,
            i18n,
            locale,
        );

        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    });

    const goto = (page, filters, anchor) => {
        const pageUrl = page
            ? (typeof page.join === "function" ? page.join("/") : page).replace(/\/+/g, "/")
            : null;
        const urlFilters = filters
            ? Array.isArray(filters)
                ? stringifyFilter(filters)
                : stringifyFilter([filters])
            : "";
        const localePrefixed = pageUrl
            ? `${locale ? `/${locale}${!pageUrl.match(/^\//) ? "/" : ""}` : ""}`
            : null;
        const gotoPage = pageUrl
            ? `${localePrefixed}${pageUrl}${
                  urlFilters.length ? `?filter=${urlFilters}&resetFilters=${Date.now()}` : ""
              }`
            : `${location.pathname}?filter=${urlFilters}&resetFilters=${Date.now()}${
                  anchor ? `#${anchor}` : ""
              }`;
        history.push(gotoPage);
    };

    const { organizationRequests } =
        fetch.results[
            `${locale}_get_organization_requested_data_organizationId_${organizationId}`
        ] || [];

    const fiscalYearsReportData = (organizationRequests || [])
        .filter((elem) => elem.status === "done")
        .map((elem) => {
            const { fiscalYear } = elem || {};
            return fiscalYear;
        });

    const fiscalYears = (fiscalYearsReportData || []).reduce((acc, elem) => {
        acc[elem] = [
            {
                id: elem,
                location: "overview",
            },
        ];
        return acc;
    }, {});

    const handleHover = (state) => {
        setHover(state);
    };

    const metadata = getOrganizationName();

    return (
        <div className={styles["app"]}>
            <Provider
                user={userInformation}
                proxy={proxy}
                theme={theme}
                uploads={uploads}
                actions={{
                    pushModal,
                    pushDialog,
                    popModal,
                    uploadFiles,
                    submitTicket,
                    submitAuditNote,
                    removeModal,
                    logout,
                    setLocale: (locale) => {
                        history.push(`/${locale}${history.location.pathname.substring(3)}`);
                    },
                    goto,
                }}
                i18n={i18n}
                locale={locale || localesByAppTheme[0].id}
                locales={localesByAppTheme}
                reload={reload}
                params={params}
                history={history}
                location={location}
            >
                <div
                    onMouseEnter={() => (menu === "collapsed" ? handleHover(true) : null)}
                    onMouseLeave={() => (menu === "collapsed" ? handleHover(false) : null)}
                    className={`${styles["aside"]} ${
                        menu === "expanded" ? "" : styles["is-collapsed"]
                    }`}
                >
                    <AsideMenu
                        logo={logo}
                        logout={logout}
                        // eslint-disable-next-line no-undef
                        title={
                            menu === "expanded" || hover
                                ? i18n`Organization ${organizationId}`
                                : " "
                        }
                        navigation={navigation}
                        locales={localesByAppTheme}
                        i18n={i18n}
                        locale={locale}
                        history={history}
                        expanded={menu === "expanded"}
                        version={version}
                        params={{
                            ...match.params,
                        }}
                        toggleMenu={toggleMenu}
                        hover={hover}
                        setHover={setHover}
                        reservedRights={configsByTheme(appTheme)["reservedRights"]}
                        applicationName={configsByTheme(appTheme)["loginApplicationName"]}
                        logoHorizontalDisplay={logoHorizontalDisplay}
                    />
                </div>
                <div className={styles["main"]}>
                    <Header
                        className={styles["header"]}
                        logout={logout}
                        navigation={navigation}
                        locales={localesByAppTheme}
                        i18n={i18n}
                        locale={locale}
                        search={false}
                        history={history}
                        fiscalYears={fiscalYears}
                        showPeriods={true}
                        params={{
                            ...match.params,
                        }}
                        theme={theme}
                        title={i18n`Overview`}
                        toggleMenu={toggleMenu}
                        expanded={menu !== "expanded"}
                        menuLogo={logo}
                        displaySupportIcon={isToDisplaySupportIcon}
                        environment={ticketSubjectEnvironment}
                        emailFrom={userInformation.email}
                        name={userInformation.name}
                        metadata={metadata}
                        freshDeskModalTitle={freshDeskFormTitleByAppTheme}
                    />
                    <main className={`${styles["content"] || ""} ${styles["foreground"] || ""}`}>
                        {children}
                    </main>
                    <ModalController />
                </div>
                <DialogController i18n={i18n} />
            </Provider>
        </div>
    );
};

OverviewDashboard.queries = (args) => {
    const { organizationId, fiscalYear, period } = args || {};

    return [
        {
            resource: "generic_organization_dashboard",
            args: {
                organizationId: organizationId,
                fiscalYear: fiscalYear,
                period: period !== "full" ? period : "",
            },
        },
        {
            resource: "user_projects_organizations",
            body: "id,name,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive",
        },
        {
            resource: "get_organization_requested_data",
            args: {
                organizationId: organizationId,
            },
            body: "organizationRequests{fiscalYear, status}",
        },
    ];
};

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        uploadFiles: uploadAction,
        submitTicket: submitTicketFormData,
        submitAuditNote: auditNoteSubmit,
    },
)(withRouter(OverviewDashboard));
